import React, { useState, useEffect, useMemo, useRef } from 'react';
import axios from 'axios';
import { t } from 'i18next';
import Modal from '@mui/material/Modal';
import Box from '@mui/material/Box';
import { useSnackbar } from 'notistack';
import { modalBaseStyle } from '../util/modalStyle';
import AppSelect from '../util/AppSelect';

interface Props {
  open: boolean;
  onClose: () => void;
  onSaved?: () => void;
  allowPartial?: boolean;   // company.hr_allowPartialDayAbsence
  users?: any[];            // HR mode: pick any employee
  editing?: any | null;     // edit an existing record
  timeStep?: number;        // company.hr_timeStepMinutes
  selfMode?: boolean;       // self-request (dashboard): employee/supervisor locked
  defaultCategory?: string;
}

interface ScheduleDay { dayOff: boolean; start: string; end: string; breakMins: number; }
interface Person { id: number; first_name: string; last_name: string; }

const CATEGORIES = ['leave', 'sickness', 'lateness'] as const;
const WD = ['M', 'T', 'W', 'T', 'F', 'S', 'S'];
const FALLBACK_START = '09:00', FALLBACK_END = '17:00';
const AV_COLORS = ['#2563eb', '#7c3aed', '#059669', '#d97706', '#0891b2', '#dc2626', '#0d9488'];

const pad = (n: number) => String(n).padStart(2, '0');
const iso = (d: Date) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const parse = (s: string) => new Date(`${s}T00:00:00`);
const wdMon0 = (d: Date) => (d.getDay() + 6) % 7;
const toMin = (hm: string) => { const [h, m] = hm.split(':').map(Number); return h * 60 + (m || 0); };
const r2 = (x: number) => Math.round(x * 100) / 100;
const fmtHours = (h: number) => { const m = Math.round(h * 60); return m % 60 ? `${Math.floor(m / 60)}h ${m % 60}m` : `${m / 60}h`; };

const Avatar: React.FC<{ p?: Person | null }> = ({ p }) => {
  const ini = p ? `${p.first_name?.[0] ?? ''}${p.last_name?.[0] ?? ''}`.toUpperCase() : '—';
  return <span className="arm-av" style={{ background: p ? AV_COLORS[p.id % AV_COLORS.length] : 'var(--btn-user-in)' }}>{ini}</span>;
};

const LockIcon = () => (
  <svg className="arm-lock" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="5" y="11" width="14" height="10" rx="2" /><path d="M8 11V7a4 4 0 0 1 8 0v4" /></svg>
);

const AbsenceRequestModal: React.FC<Props> = ({ open, onClose, onSaved, allowPartial = false, users = [], editing, timeStep = 15, selfMode = false, defaultCategory = 'leave' }) => {
  const { enqueueSnackbar } = useSnackbar();
  const [types, setTypes] = useState<any[]>([]);
  const [patterns, setPatterns] = useState<any[]>([]);
  const [holidays, setHolidays] = useState<Set<string>>(new Set());
  const [me, setMe] = useState<any>(null);
  const [mySupervisor, setMySupervisor] = useState<Person | null>(null);
  const [balances, setBalances] = useState<any[]>([]);

  const [userId, setUserId] = useState('');
  const [approverId, setApproverId] = useState('');
  const [typeId, setTypeId] = useState('');
  const [category, setCategory] = useState(defaultCategory);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [customTimes, setCustomTimes] = useState<{ start: string; end: string } | null>(null);
  const [minutesLate, setMinutesLate] = useState('');
  const [note, setNote] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [docUrl, setDocUrl] = useState<string | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const [month, setMonth] = useState(() => { const d = new Date(); return new Date(d.getFullYear(), d.getMonth(), 1); });
  const [picking, setPicking] = useState(false);
  const [loading, setLoading] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  const isEdit = !!editing?.id;
  const company = me?.company ?? {};
  const effAllowPartial = selfMode ? !!company.hr_allowPartialDayAbsence : allowPartial;
  const effTimeStep = selfMode ? (company.hr_timeStepMinutes ?? 15) : timeStep;

  // ── load reference data ──────────────────────────────────────────────────────
  useEffect(() => {
    if (!open) return;
    axios.get('hr-absence-type').then(r => setTypes((Array.isArray(r.data) ? r.data : []).filter((x: any) => x.isActive))).catch(() => {});
    axios.get('hr-public-holiday').then(r => setHolidays(new Set((Array.isArray(r.data) ? r.data : []).map((h: any) => h.date)))).catch(() => {});
    axios.get('user').then(r => setMe(r.data)).catch(() => {});
    if (selfMode) {
      axios.get('hr-absence/my-supervisor').then(r => setMySupervisor(r.data || null)).catch(() => {});
    } else {
      axios.get('hr-schedule-template').then(r => setPatterns(Array.isArray(r.data) ? r.data : [])).catch(() => {});
    }

    const today = iso(new Date());
    if (editing?.id) {
      setUserId(String(editing.userId ?? ''));
      setApproverId(editing.approverId ? String(editing.approverId) : '');
      setCategory(editing.category || 'leave');
      setTypeId(String(editing.typeId ?? ''));
      setStartDate(editing.startDate || today);
      setEndDate(editing.endDate || editing.startDate || today);
      setCustomTimes(editing.startTime && editing.endTime ? { start: editing.startTime.slice(0, 5), end: editing.endTime.slice(0, 5) } : null);
      setMinutesLate(editing.minutesLate != null ? String(editing.minutesLate) : '');
      setNote(editing.note || '');
      setDocUrl(editing.documentUrl || null);
      const d = parse(editing.startDate || today);
      setMonth(new Date(d.getFullYear(), d.getMonth(), 1));
    } else {
      setUserId(''); setApproverId(''); setCategory(defaultCategory); setTypeId('');
      setStartDate(today); setEndDate(today); setCustomTimes(null);
      setMinutesLate(''); setNote(''); setDocUrl(null);
      const d = new Date(); setMonth(new Date(d.getFullYear(), d.getMonth(), 1));
    }
    setFile(null); setPicking(false);
  }, [open, editing, selfMode, defaultCategory]);

  // the employee the request is for
  const subjectId = selfMode ? me?.id : (userId ? Number(userId) : null);
  const selUser = !selfMode ? users.find(x => String(x.id) === userId) : null;

  useEffect(() => {
    if (!open || !subjectId) { setBalances([]); return; }
    const url = selfMode ? 'hr-absence/my-balance' : `hr-absence/user-balance/${subjectId}`;
    axios.get(url).then(r => setBalances(Array.isArray(r.data) ? r.data : [])).catch(() => setBalances([]));
  }, [open, subjectId, selfMode]);

  // default type on category change (create only)
  useEffect(() => {
    if (isEdit || !types.length) return;
    const def = types.find(x => x.isDefault && x.category === category) ?? types.find(x => x.category === category);
    setTypeId(def ? String(def.id) : '');
  }, [category, types, isEdit]);

  // HR mode: picking an employee pre-fills their supervisor (still editable)
  useEffect(() => {
    if (isEdit || selfMode || !userId) return;
    const u = users.find(x => String(x.id) === userId);
    setApproverId(u?.supervisorId ? String(u.supervisorId) : '');
  }, [userId, users, isEdit, selfMode]);

  // ── work pattern of the subject ──────────────────────────────────────────────
  const pattern = selfMode ? me?.pattern : patterns.find(p => p.id === selUser?.patternId);
  const patternDay = (d: Date): ScheduleDay | null => {
    const days = pattern?.days;
    return Array.isArray(days) && days[wdMon0(d)] ? days[wdMon0(d)] : null;
  };
  // Same rules as backend hr_absence.service dayHours(): days jsonb → weekday columns → Mon–Fri 8h
  const dayHours = (d: Date) => {
    const pd = patternDay(d);
    if (pd) return pd.dayOff ? 0 : Math.max(0, toMin(pd.end || '0:0') - toMin(pd.start || '0:0') - (pd.breakMins || 0)) / 60;
    if (pattern) return pattern[['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'][wdMon0(d)]] ?? 0;
    return wdMon0(d) < 5 ? 8 : 0;
  };
  const dayStart = (d: Date) => { const pd = patternDay(d); return pd && !pd.dayOff && pd.start ? pd.start : FALLBACK_START; };
  const dayEnd = (d: Date) => { const pd = patternDay(d); return pd && !pd.dayOff && pd.end ? pd.end : FALLBACK_END; };
  const excludedHoliday = (s: string) => holidays.has(s) && (pattern?.public_holiday_policy || 'deducted') !== 'works';

  // ── derived ──────────────────────────────────────────────────────────────────
  const isLateness = category === 'lateness';
  const effEnd = isLateness ? startDate : endDate;
  const rangeValid = !!startDate && !!effEnd && effEnd >= startDate;
  const singleDay = rangeValid && startDate === effEnd;
  const canEditTimes = effAllowPartial && singleDay && !isLateness;
  const defaultTimes = startDate && effEnd ? { start: dayStart(parse(startDate)), end: dayEnd(parse(effEnd)) } : { start: FALLBACK_START, end: FALLBACK_END };
  const times = canEditTimes && customTimes ? customTimes : defaultTimes;
  const timesEdited = canEditTimes && !!customTimes && (customTimes.start !== defaultTimes.start || customTimes.end !== defaultTimes.end);
  const timeError = timesEdited && toMin(times.end) <= toMin(times.start);

  // working days / hours — mirrors backend computeHoursDays (working-days basis)
  const calc = useMemo(() => {
    if (!rangeValid || isLateness) return { days: 0, hours: 0 };
    if (timesEdited) {
      const step = effTimeStep > 1 ? effTimeStep : 1;
      const mins = Math.round(Math.max(0, toMin(times.end) - toMin(times.start)) / step) * step;
      const full = dayHours(parse(startDate));
      return { hours: mins / 60, days: full > 0 ? r2(mins / 60 / full) : 0 };
    }
    let days = 0, hours = 0;
    for (const d = parse(startDate); d <= parse(effEnd); d.setDate(d.getDate() + 1)) {
      const h = dayHours(d);
      if (h > 0 && !excludedHoliday(iso(d))) { days += 1; hours += h; }
    }
    return { days, hours: r2(hours) };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rangeValid, isLateness, timesEdited, times.start, times.end, startDate, effEnd, pattern, holidays, effTimeStep]);

  const nonWorking = category === 'leave' && rangeValid && !timesEdited && calc.hours <= 0;
  const calendarDays = rangeValid ? Math.round((parse(effEnd).getTime() - parse(startDate).getTime()) / 86400000) + 1 : 0;
  const fitNoteRequired = category === 'sickness' && calendarDays > 7;

  const balance = category === 'leave' ? balances.find(b => String(b.typeId) === typeId) : null;
  const inHours = balance?.displayUnit === 'hours';
  const balTotal = balance ? (inHours ? balance.entitlementHours : balance.entitlementDays) : null;
  const balLeft = balance ? (inHours ? balance.remainingHours : balance.remainingDays) : null;
  const reqAmount = inHours ? calc.hours : calc.days;
  // Types without a limit (unpaid etc. come with entitlement 0) have no balance to show
  const showBalance = !!balance && balTotal != null && balTotal > 0 && balLeft != null;
  const overBalance = !!showBalance && !isEdit && reqAmount > (balLeft as number);

  const endBeforeStart = !!startDate && !!effEnd && effEnd < startDate;
  const missing = (!selfMode && !userId) || !typeId || !startDate || !effEnd || (isLateness && !minutesLate);
  const blocked = loading || missing || endBeforeStart || timeError || nonWorking;

  // ── calendar ─────────────────────────────────────────────────────────────────
  const cells = useMemo(() => {
    const first = new Date(month);
    const lead = wdMon0(first);
    const count = new Date(month.getFullYear(), month.getMonth() + 1, 0).getDate();
    return [...Array(lead).fill(null), ...Array.from({ length: count }, (_, i) => new Date(month.getFullYear(), month.getMonth(), i + 1))];
  }, [month]);

  const pickDay = (d: Date) => {
    const s = iso(d);
    if (isLateness || !picking || s < startDate) {
      setStartDate(s); setEndDate(s); setPicking(!isLateness);
    } else {
      setEndDate(s); setPicking(false);
    }
    setCustomTimes(null);
  };

  const setDateField = (which: 'start' | 'end', v: string) => {
    if (!v) return;
    if (which === 'start') { setStartDate(v); if (!endDate || v > endDate || isLateness) setEndDate(v); }
    else setEndDate(v);
    const d = parse(v); setMonth(new Date(d.getFullYear(), d.getMonth(), 1));
    setCustomTimes(null); setPicking(false);
  };

  const setTime = (which: 'start' | 'end', v: string) => {
    if (!v) return;
    setCustomTimes({ ...times, [which]: v });
  };

  // ── submit ───────────────────────────────────────────────────────────────────
  const handleSubmit = async () => {
    if (missing) { enqueueSnackbar(t('FillRequired') as string, { variant: 'error' }); return; }
    if (blocked) return;
    setLoading(true);
    try {
      let documentUrl = docUrl;
      if (file) {
        const fd = new FormData();
        fd.append('file', file);
        const { data } = await axios.post('hr-absence/upload', fd);
        documentUrl = data?.url ?? null;
      }
      const payload: any = {
        typeId: Number(typeId), category, startDate, endDate: effEnd,
        note: note.trim() || null,
        documentUrl,
        startTime: null, endTime: null, minutesLate: null,
      };
      if (!selfMode) {
        payload.userId = Number(userId);
        payload.approverId = approverId ? Number(approverId) : null;
      }
      if (!isEdit) payload.status = category === 'leave' ? 'pending' : 'recorded';
      if (isLateness) payload.minutesLate = Number(minutesLate) || 0;
      else if (timesEdited) { payload.startTime = times.start; payload.endTime = times.end; }

      if (isEdit) await axios.put(`hr-absence/${editing.id}`, payload);
      else await axios.post('hr-absence/add', payload);
      enqueueSnackbar(t('Saved') as string, { variant: 'success' });
      onSaved?.(); onClose();
    } catch (e: any) {
      const msg = e?.response?.data?.message;
      enqueueSnackbar((msg === 'non_working_day' ? t('HrNonWorkingDay') : t('Error')) as string, { variant: 'error' });
    } finally { setLoading(false); }
  };

  // ── header people ────────────────────────────────────────────────────────────
  const employee: Person | null = selfMode ? me : (selUser ?? null);
  const supervisor: Person | null = selfMode ? mySupervisor : (users.find(x => String(x.id) === approverId) ?? null);
  const hrEdit = !selfMode;
  const subtitle = selfMode
    ? t('HrMyRequest') as string
    : (selUser ? t('HrForEmployee', { name: `${selUser.first_name} ${selUser.last_name}` }) as string : '');

  const today = iso(new Date());
  const unit = inHours ? 'h' : (t('HrDaysShort') as string);
  const fmtAmount = (n: number) => `${r2(n)}${inHours ? 'h' : ''}`;

  const pickFile = (f?: File | null) => { if (f) { setFile(f); setDocUrl(null); } };

  return (
    // disableEnforceFocus: AppSelect's search box renders in a portal outside the modal
    <Modal open={open} onClose={onClose} disableEnforceFocus>
      <Box className="arm tm-scope" sx={{ ...modalBaseStyle, p: 0, top: '50%', left: '50%', transform: 'translate(-50%, -50%)' }}>
        {/* Header */}
        <div className="arm-head" style={selfMode ? { paddingTop: 12, paddingBottom: 10 } : undefined}>
          <div className="arm-ic"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><circle cx="12" cy="12" r="4" /><path d="M12 2v2M12 20v2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M2 12h2M20 12h2M4.9 19.1l1.4-1.4M17.7 6.3l1.4-1.4" /></svg></div>
          <div className="arm-titles">
            <div className="arm-title">{isEdit ? t('HrEditAbsence') : t('HrAddAbsence')}</div>
            <div className="arm-sub">{subtitle || ' '}</div>
          </div>
          {!selfMode && (
            <div className="arm-people">
              <div className="arm-person">
                <label>{t('HrEmployee')}</label>
                <div className={`arm-pbox${hrEdit && !isEdit ? '' : ' locked'}`}>
                  <Avatar p={employee} />
                  {hrEdit && !isEdit ? (
                    <AppSelect className="arm-asel" searchable value={userId || null} placeholder={t('SelectEmployee') as string}
                      onChange={v => setUserId(v == null ? '' : String(v))}
                      options={users.map(u => ({ value: u.id, label: `${u.first_name} ${u.last_name}` }))} />
                  ) : (<><span className="arm-pname">{employee ? `${employee.first_name} ${employee.last_name}` : '—'}</span><LockIcon /></>)}
                </div>
              </div>
              <div className="arm-person">
                <label>{t('Supervisor')}</label>
                <div className={`arm-pbox${hrEdit ? '' : ' locked'}`}>
                  <Avatar p={supervisor} />
                  {hrEdit ? (
                    <AppSelect className="arm-asel" searchable value={approverId || null} placeholder="—"
                      onChange={v => setApproverId(v == null ? '' : String(v))}
                      options={users.filter(u => String(u.id) !== userId).map(u => ({ value: u.id, label: `${u.first_name} ${u.last_name}` }))} />
                  ) : (<><span className="arm-pname">{supervisor ? `${supervisor.first_name} ${supervisor.last_name}` : '—'}</span><LockIcon /></>)}
                </div>
              </div>
            </div>
          )}
          <button type="button" className="arm-x" onClick={onClose} aria-label="Close">✕</button>
        </div>

        {/* Body: left = calendar + note, right = everything else */}
        <div className="arm-body">
          <div className="arm-left">
            <div className="arm-cal">
              <div className="arm-cal-head">
                <button type="button" onClick={() => setMonth(new Date(month.getFullYear(), month.getMonth() - 1, 1))}>‹</button>
                <span>{month.toLocaleDateString('en-GB', { month: 'long', year: 'numeric' })}</span>
                <button type="button" onClick={() => setMonth(new Date(month.getFullYear(), month.getMonth() + 1, 1))}>›</button>
              </div>
              <div className="arm-cal-grid">
                {WD.map((w, i) => <span key={i} className="arm-wd">{w}</span>)}
                {cells.map((d, i) => {
                  if (!d) return <span key={i} />;
                  const s = iso(d);
                  const inRange = rangeValid && s >= startDate && s <= effEnd;
                  const edge = s === startDate || s === effEnd;
                  const off = dayHours(d) <= 0;
                  return (
                    <button key={i} type="button" onClick={() => pickDay(d)}
                      className={`arm-day${off ? ' off' : ''}${inRange ? ' in' : ''}${inRange && edge ? ' edge' : ''}${s === today ? ' today' : ''}`}>
                      {d.getDate()}
                    </button>
                  );
                })}
              </div>
            </div>
            <div className="arm-field arm-note">
              <label>{t('Note')} <em>({t('Optional')})</em></label>
              <textarea value={note} onChange={e => setNote(e.target.value)} placeholder={t('HrNotePlaceholder') as string} />
            </div>
          </div>

          <div className="arm-right">
            <div className="arm-row1">
              <div className="arm-cats">
                {CATEGORIES.map(c => (
                  <button key={c} type="button" className={`arm-cat ${c}${category === c ? ' on' : ''}`} disabled={isEdit && category !== c}
                    onClick={() => { setCategory(c); setCustomTimes(null); if (c === 'lateness') setEndDate(startDate); }}>
                    {t('Hr' + c.charAt(0).toUpperCase() + c.slice(1))}
                  </button>
                ))}
              </div>
              <div className="arm-field">
                <label>{t('HrAbsenceType')}</label>
                <AppSelect className="arm-asel" value={typeId || null} placeholder="—"
                  onChange={v => setTypeId(v == null ? '' : String(v))}
                  options={types.filter(tp => tp.category === category).map(tp => ({ value: tp.id, label: tp.name }))} />
              </div>
            </div>

            {isLateness ? (
              <div className="arm-dates">
                <div className="arm-field"><label>{t('Date')}</label>
                  <input type="date" value={startDate} onChange={e => setDateField('start', e.target.value)} /></div>
                <div className="arm-field"><label>{t('HrMinutesLate')}</label>
                  <input type="number" min={1} value={minutesLate} onChange={e => setMinutesLate(e.target.value)} /></div>
              </div>
            ) : (
              <div className="arm-dates">
                <div className="arm-field"><label>{t('Start')}</label>
                  <div className="arm-dt">
                    <input type="date" value={startDate} onChange={e => setDateField('start', e.target.value)} />
                    <input type="time" step={effTimeStep * 60} value={times.start} disabled={!canEditTimes} onChange={e => setTime('start', e.target.value)} />
                  </div>
                </div>
                <div className="arm-field">
                  <label>{t('End')}{timesEdited && <button type="button" className="arm-reset" onClick={() => setCustomTimes(null)}>{t('HrResetFullDay')}</button>}</label>
                  <div className="arm-dt">
                    <input type="date" value={endDate} min={startDate} onChange={e => setDateField('end', e.target.value)} />
                    <input type="time" step={effTimeStep * 60} value={times.end} disabled={!canEditTimes} onChange={e => setTime('end', e.target.value)} />
                  </div>
                </div>
              </div>
            )}

            <div className="arm-field arm-file-wrap">
              <label>{category === 'sickness' ? t('HrFitNote') : t('HrAttachment')} {!fitNoteRequired && <em>({t('Optional')})</em>}</label>
              <div className={`arm-drop${dragOver ? ' over' : ''}${fitNoteRequired && !file && !docUrl ? ' req' : ''}`}
                onDragOver={e => { e.preventDefault(); setDragOver(true); }}
                onDragLeave={() => setDragOver(false)}
                onDrop={e => { e.preventDefault(); setDragOver(false); pickFile(e.dataTransfer.files?.[0]); }}
                onClick={() => fileRef.current?.click()}>
                {file || docUrl ? (
                  <span className="arm-chip" onClick={e => e.stopPropagation()}>
                    📎 {file ? file.name : (docUrl as string).split('/').pop()}
                    <button type="button" onClick={() => { setFile(null); setDocUrl(null); }}>✕</button>
                  </span>
                ) : (
                  <>
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M12 16V4M7 9l5-5 5 5M4 20h16" /></svg>
                    <span>{t('HrDropFile')} <b>{t('HrBrowse')}</b></span>
                    <small>{fitNoteRequired ? t('HrFitNoteRequired') : t('HrFileTypes')}</small>
                  </>
                )}
                <input ref={fileRef} type="file" hidden accept=".pdf,.jpg,.jpeg,.png" onChange={e => pickFile(e.target.files?.[0])} />
              </div>
            </div>

          </div>
        </div>

        {/* Footer */}
        <div className="arm-foot">
          <div className="arm-sum">
            {endBeforeStart ? <b className="r">{t('HrEndBeforeStart')}</b>
              : timeError ? <b className="r">{t('HrEndBeforeStart')}</b>
              : nonWorking ? <b className="r">{t('HrNonWorkingDay')}</b>
              : overBalance ? <b className="r">{t('HrOverBalance')}</b>
              : isLateness ? <b>{minutesLate ? `${minutesLate} min` : '—'}</b>
              : rangeValid ? <b>{timesEdited ? fmtHours(calc.hours) : t('HrWorkingDaysN', { count: calc.days })}</b> : null}
            {showBalance && !isLateness && (
              <small>{t('HrLeftAfterRequest', { left: fmtAmount((balLeft as number) - reqAmount), total: fmtAmount(balTotal as number), unit })}</small>
            )}
          </div>
          <button type="button" className="dt-btn-ghost" onClick={onClose}>{t('Cancel')}</button>
          <button type="button" className="btn-primary" onClick={handleSubmit} disabled={blocked}>
            {!isEdit && category === 'leave' ? t('HrSendRequest') : t('Save')}
          </button>
        </div>
      </Box>
    </Modal>
  );
};

export default AbsenceRequestModal;
